import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FreeAgentTmValueCard from "@/components/FreeAgentTmValueCard";
import PositionField from "@/components/PositionField";
import { RequestUpdateButtons } from "./team-detail/rosterRows";
import { STATUS_OPTIONS } from "./team-detail/admin";
import {
    trStat, trFoot, trForm, trInjury,
    trWeakFootUsage, trWeakFootAccuracy, trStyle, trPositionFull, trPositionShort,
    trSkill, trAiStyle, PLAYING_STYLE_IT,
} from "@/lib/pesTranslations";

// Lista unica degli stili di gioco PES (dedup per etichetta IT, ordinata) per le tendine admin.
const STYLE_OPTIONS = (() => {
    const seen = new Set();
    const out = [];
    for (const [en, it] of Object.entries(PLAYING_STYLE_IT)) {
        if (seen.has(it)) continue;
        seen.add(it);
        out.push({ value: en, label: it });
    }
    out.sort((a, b) => a.label.localeCompare(b.label, "it"));
    return out;
})();

// Layout statistiche stile eFootball: 3 colonne (Attacco · Difesa · Forza).
// La Difesa include i 5 valori del Portiere, esattamente come nella scheda originale.
// "Valori authentic di riferimento": statistiche PESDB che coincidono con i
// valori riassuntivi PES. Evidenziate con ◆ (senza toccare i colori dei valori).
const REF_STATS = new Set([
    "Finishing", "Low Pass", "Ball Control", "Acceleration", "Speed", "Physical Contact", "Tackling",
]);
const REF_STATS_GK = new Set(["GK Awareness", "GK Parrying", "GK Reflexes"]);

const STAT_COLUMNS_EF = [
    {
        title: "Attacco",
        keys: [
            "Offensive Awareness", "Ball Control", "Dribbling", "Tight Possession",
            "Low Pass", "Lofted Pass", "Finishing", "Heading", "Set Piece Taking", "Curl",
        ],
    },
    {
        title: "Difesa",
        keys: [
            "Defensive Awareness", "Tackling", "Aggression", "Defensive Engagement",
            "GK Awareness", "GK Catching", "GK Parrying", "GK Reflexes", "GK Reach",
        ],
    },
    {
        title: "Forza",
        keys: [
            "Speed", "Acceleration", "Kicking Power", "Jumping",
            "Physical Contact", "Balance", "Stamina",
        ],
    },
];

export default function PlayerDetail() {
    const { pesId } = useParams();
    const { user } = useAuth();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCompare, setShowCompare] = useState(false);
    const [careerStats, setCareerStats] = useState([]);

    const loadPlayer = () => {
        return api.get(`/players/${pesId}`)
            .then((r) => setP(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Giocatore non trovato"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadPlayer();
        api.get(`/players/${pesId}/career-stats`)
            .then((r) => setCareerStats(r.data?.career_stats || []))
            .catch(() => setCareerStats([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pesId]);

    if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 op-label">Caricamento…</div>;
    if (error) return (
        <div className="max-w-7xl mx-auto px-4 py-20">
            <div className="op-card p-10 text-center">
                <div className="op-label text-tff3b30">{error}</div>
                <Link to="/giocatori" className="op-btn mt-5 inline-flex">← Torna ai giocatori</Link>
            </div>
        </div>
    );
    if (!p) return null;

    const stats = p.stats || {};
    const hasDetails = p.details_synced;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="player-detail">
            <Link to="/giocatori" className="op-label text-t8f95a3 hover:text-tffffff inline-block mb-6" data-testid="back-to-players">
                ← Tutti i giocatori
            </Link>

            <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-start">
                {/* card image + overall */}
                <div className="op-card p-6 text-center">
                    <div
                        className="w-full bg-t0d0f12 border border-t22252a overflow-hidden mx-auto flex items-center justify-center mb-4 relative"
                        style={{ aspectRatio: "200 / 283" }}
                    >
                        {p.image_url ? (
                            <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" />
                        ) : (
                            <div className="op-heading text-6xl text-t5c6370">{p.name?.charAt(0)}</div>
                        )}
                        {/* TM headshot inset (real photo) */}
                        {p.op_team?.tm_image && (
                            <img
                                src={p.op_team.tm_image}
                                alt={`${p.name} (real)`}
                                className="absolute bottom-2 right-2 w-14 h-14 rounded-full object-cover border-2 border-t2bb6ff bg-t1a1d24"
                                style={{ objectPosition: "center 20%" }}
                                title="Foto reale (Transfermarkt)"
                                data-testid="player-tm-photo"
                            />
                        )}
                    </div>
                    <div className="op-heading text-7xl text-t2bb6ff" data-testid="player-overall">
                        {p.overall ?? "—"}
                    </div>
                    <div className="op-label mt-1">
                        VG · <span title={trPositionFull(p.position)}>{trPositionShort(p.position)}</span>
                    </div>
                </div>

                {/* info */}
                <div>
                    <div className="op-label text-t2bb6ff flex flex-wrap items-center gap-3">
                        <span>{p.no_pesdb ? "// GIOCATORE SENZA SCHEDA PESDB" : `// PROFILO PES DB · ID ${p.pes_id}`}</span>
                        {p.dead_pesdb && user?.role === "admin" && (
                            <Link
                                to="/admin/pesdb-relink"
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-tff9800 text-tff9800 hover:bg-tff9800 hover:text-black transition-colors op-mono text-[10px] uppercase tracking-wider normal-case"
                                title={`ID PESDB ${p.pes_id} non più valido: cerca il nuovo id e ricollega`}
                                data-testid="player-relink-badge"
                            >
                                ⚠ Da ri-agganciare
                            </Link>
                        )}
                        {user?.role === "admin" && typeof p.pes_id === "number" && (
                            p.is_duplicate ? (
                                <button
                                    onClick={async () => {
                                        try { await api.post(`/players/${p.pes_id}/unhide-duplicate`); loadPlayer(); } catch (e) {}
                                    }}
                                    className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black transition-colors op-mono text-[10px] uppercase tracking-wider normal-case"
                                    title="Ripristina: questa carta tornerà visibile in ricerca e svincolati"
                                    data-testid="player-unhide-duplicate-btn"
                                >
                                    ♻ Ripristina carta
                                </button>
                            ) : (
                                <button
                                    onClick={async () => {
                                        if (!window.confirm(`Nascondere "${p.name}" come doppione?\n\nLa carta sparirà da ricerca globale, lista giocatori e svincolati. È reversibile (Admin → PES DB Sync → Doppioni nascosti).`)) return;
                                        try { await api.post(`/players/${p.pes_id}/hide-duplicate`); loadPlayer(); } catch (e) {}
                                    }}
                                    className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-tff7a1a text-tff7a1a hover:bg-tff7a1a hover:text-black transition-colors op-mono text-[10px] uppercase tracking-wider normal-case"
                                    title="Nascondi questa carta come doppione (es. carta nazionale)"
                                    data-testid="player-hide-duplicate-btn"
                                >
                                    ⊘ Nascondi doppione
                                </button>
                            )
                        )}
                    </div>
                    <h1 className="op-heading text-5xl sm:text-6xl mt-2 mb-2" data-testid="player-name">
                        {p.name}
                    </h1>
                    <div className="op-mono text-sm text-t8f95a3 mb-3">
                        {trPositionFull(p.position)}
                    </div>

                    <a
                        href={p.tm_link || p.op_team?.tm_link || `https://www.transfermarkt.it/schnellsuche/ergebnis/schnellsuche?query=${encodeURIComponent(p.name || "")}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-2 mb-4 px-4 py-2 border border-t00bfff/40 bg-t1a1d24 hover:border-t00bfff transition-colors"
                        data-testid="player-tm-link"
                    >
                        <span className="op-mono text-[10px] uppercase text-t8f95a3">Transfermarkt</span>
                        <span className="op-heading text-sm text-t00bfff">Vedi prezzo su TM ↗</span>
                    </a>

                    {p.pes_id && !p.no_pesdb && !p.dead_pesdb && (
                        <a
                            href={`https://pesdb.net/efootball/?id=${p.pes_id}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-2 mb-4 ml-3 px-4 py-2 border border-t7ce38b/40 bg-t1a1d24 hover:border-t7ce38b transition-colors"
                            data-testid="player-pesdb-link"
                        >
                            <span className="op-mono text-[10px] uppercase text-t8f95a3">PES DB</span>
                            <span className="op-heading text-sm text-t7ce38b">Vedi su PES DB ↗</span>
                        </a>
                    )}

                    <button
                        onClick={() => setShowCompare(true)}
                        className="inline-flex items-center gap-2 mb-4 ml-3 px-4 py-2 border border-tb98bff/50 bg-tb98bff/10 hover:bg-tb98bff/20 transition-colors align-top"
                        data-testid="compare-open-btn"
                    >
                        <span className="op-mono text-[10px] uppercase text-t8f95a3">Confronto</span>
                        <span className="op-heading text-sm text-tb98bff">⚖️ Confronta giocatore</span>
                    </button>
                    {showCompare && <ComparePanel playerA={p} onClose={() => setShowCompare(false)} />}

                    {/* Original PES team affiliation (or Free Transfer) */}
                    <OpTeamBanner op={p.op_team} />

                    {p.op_team?.listing && (
                        <Link
                            to={`/mercato?section=vetrina&team=${p.op_team.listing.team_id || p.op_team.id}&highlight=${p.op_team.entry_id || ""}`}
                            className="mb-6 ml-3 inline-flex items-center gap-2 px-4 py-2 border border-te8ff00/50 bg-te8ff00/10 align-top hover:bg-te8ff00/20 transition-colors"
                            data-testid="player-in-vetrina-badge"
                            title="Apri la Vetrina del Mercato per vedere questo annuncio"
                        >
                            <span className="op-mono text-[10px] uppercase text-t8f95a3">Mercato</span>
                            <span className="op-heading text-sm text-te8ff00">
                                🛒 {p.op_team.listing.listing_type === "loan" ? "IN PRESTITO" : "IN VENDITA"}
                            </span>
                            <span className="op-mono text-sm text-tffffff" data-testid="player-vetrina-price">
                                {p.op_team.listing.listing_type === "loan" && !p.op_team.listing.price
                                    ? "gratis"
                                    : formatMln(p.op_team.listing.price)}
                            </span>
                            {p.op_team.listing.note && (
                                <span className="op-mono text-[10px] text-t8f95a3 italic max-w-[220px] truncate">· {p.op_team.listing.note}</span>
                            )}
                            <span className="op-mono text-[10px] text-te8ff00/70">↗ vai alla Vetrina</span>
                        </Link>
                    )}

                    {user?.role === "admin" && p.op_team?.entry_id && (
                        <AdminPesdbPanel
                            teamId={p.op_team.id}
                            entryId={p.op_team.entry_id}
                            currentPesId={p.pes_id}
                            playerName={p.name}
                            currentTmLink={p.tm_link || p.op_team?.tm_link || ""}
                            currentStatuses={p.op_team?.statuses || []}
                            currentOnLoan={!!p.op_team?.is_on_loan}
                            currentLoanedOut={!!p.op_team?.is_loaned_out}
                            currentLoanKind={p.op_team?.loan_kind || ""}
                            currentStyleAtt={p.playing_style_att || ""}
                            currentStyleDef={p.playing_style_def || ""}
                            deadPesdb={!!p.dead_pesdb}
                            noPesdb={!!p.no_pesdb}
                            onChanged={loadPlayer}
                        />
                    )}

                    {user?.role === "admin" && !p.op_team?.entry_id && (
                        <FreeAgentTmValueCard pesId={p.pes_id} onChanged={loadPlayer} />
                    )}

                    {(user?.role === "admin" || (user?.team_id && user.team_id === p.op_team?.id)) && p.op_team?.entry_id && (
                        <RequestUpdateOnCard
                            teamId={p.op_team.id}
                            entryId={p.op_team.entry_id}
                            pesId={p.pes_id}
                            tmLink={p.tm_link || p.op_team?.tm_link}
                            name={p.name}
                        />
                    )}

                    {/* Fai Offerta: visibile solo a un presidente di un'ALTRA squadra,
                        per un giocatore in rosa e NON ceduto in prestito. Riusa il
                        deep-link della pagina squadra che apre il modale offerta. */}
                    {p.op_team
                        && !p.op_team.is_loaned_out
                        && p.op_team.entry_id
                        && user?.team_id
                        && user.team_id !== p.op_team.id && (
                        <div className="mb-6 ml-3 inline-block">
                            <Link
                                to={`/squadre/${p.op_team.id}?offer=${p.op_team.entry_id}`}
                                className="op-btn inline-flex items-center gap-2 border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black transition-colors"
                                data-testid="player-make-offer-btn"
                            >
                                💸 Fai Offerta
                            </Link>
                        </div>
                    )}

                    {(p.op_team?.market_value_current ?? p.op_team?.market_value) != null && (
                        <div className="mb-6 inline-flex items-center gap-3 ml-3 px-4 py-2 border border-t00bfff/40 bg-t1a1d24" data-testid="player-tm-value">
                            <span className="op-mono text-[10px] uppercase text-t8f95a3">Valore TM</span>
                            <a
                                href={p.op_team.tm_link || "#"}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="op-heading text-base text-t00bfff hover:underline"
                                title="Apri su Transfermarkt"
                            >
                                {formatMln(p.op_team.market_value_current ?? p.op_team.market_value)}
                            </a>
                            {p.op_team.tm_value_date && (
                                <span className="op-mono text-[10px] text-t5c6370" data-testid="player-tm-date">
                                    agg. {p.op_team.tm_value_date}
                                </span>
                            )}
                        </div>
                    )}

                    {p.op_team?.contract_history?.length > 0 && (
                        <div className="op-card p-4 mb-6" data-testid="contract-history">
                            <div className="op-label text-t7ce38b mb-3">// STORICO CONTRATTI</div>
                            <div className="space-y-2">
                                {[...p.op_team.contract_history].reverse().map((h, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between gap-3 text-sm border-l-2 border-t7ce38b/40 pl-3 py-1"
                                        data-testid={`contract-history-row-${i}`}
                                    >
                                        <div>
                                            <span className="op-mono text-[11px] text-t8f95a3">
                                                {h.at ? new Date(h.at).toLocaleDateString("it-IT") : "—"}
                                            </span>
                                            <span className="ml-2 text-tc9ccd3">{h.type === "rinnovo" ? "Rinnovo" : h.type}</span>
                                            {h.contract_years != null && (
                                                <span className="ml-2 op-mono text-[10px] uppercase text-t5c6370">
                                                    {h.contract_years} {h.contract_years === 1 ? "anno" : "anni"}
                                                </span>
                                            )}
                                        </div>
                                        <div className="op-mono text-xs text-right whitespace-nowrap">
                                            {h.prev_wage != null && (
                                                <span className="text-t5c6370 line-through mr-2">{formatMln(h.prev_wage)}</span>
                                            )}
                                            <span className="text-t7ce38b font-bold">{formatMln(h.new_wage)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-t22252a divide-x divide-y sm:divide-y-0 divide-t22252a mb-6">
                        <InfoCell label="Età" value={p.age} />
                        <InfoCell label="Altezza" value={p.height ? `${p.height} cm` : "—"} />
                        <InfoCell label="Peso" value={p.weight ? `${p.weight} kg` : "—"} />
                        <InfoCell label="Piede" value={trFoot(p.foot)} />
                        <InfoCell label="Nazionalità" value={p.nationality || "—"} />
                        <InfoCell label="Club PES" value={p.team_pes || "—"} />
                    </div>

                    {hasDetails && (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                                {(p.playing_style_att || p.playing_style_def) ? (
                                    <>
                                        <Highlight label="Stile in attacco" value={p.playing_style_att ? trStyle(p.playing_style_att) : "—"} accent="rgb(var(--tc-2bb6ff))" />
                                        <Highlight label="Stile in difesa" value={p.playing_style_def ? trStyle(p.playing_style_def) : "—"} accent="rgb(var(--tc-ff7a1a))" />
                                    </>
                                ) : (
                                    <Highlight label="Stile di gioco" value={trStyle(p.playing_style)} accent="rgb(var(--tc-2bb6ff))" />
                                )}
                                <Highlight
                                    label="Resistenza infortuni"
                                    value={trInjury(p.injury_resistance)}
                                    accent={injuryColor(p.injury_resistance)}
                                />
                                <Highlight
                                    label="Piede debole"
                                    value={trWeakFootAccuracy(p.weak_foot_accuracy)}
                                    sub={trWeakFootUsage(p.weak_foot_usage)}
                                    accent="rgb(var(--tc-e8ff00))"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6 items-start">
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <SmallChip label="Forma" value={trForm(p.form)} />
                                        <SmallChip
                                            label="Stipendio"
                                            value={p.purchase_wage ? `${(p.purchase_wage / 1e6).toFixed(2)} mln` : "—"}
                                            sub={p.applied_wage && p.applied_wage !== p.purchase_wage ? `in rosa: ${(p.applied_wage / 1e6).toFixed(2)} mln` : null}
                                        />
                                    </div>
                                    <SkillsCard skills={p.player_skills} aiStyles={p.ai_playing_styles} />
                                </div>
                                <PositionField pesId={p.pes_id} compact />
                            </div>
                        </>
                    )}

                    {!hasDetails && (
                        <div className="op-card p-4 border-te8ff00/30 mb-6" data-testid="no-details-note">
                            {p.no_pesdb ? (
                                <>
                                    <div className="op-label text-tff7a1a">// DATI PESDB NON DISPONIBILI</div>
                                    <p className="text-tc9ccd3 text-sm mt-2">
                                        Questo giocatore non è presente su PESDB, quindi VG, abilità,
                                        statistiche e stile di gioco non sono disponibili. Restano
                                        comunque nome, foto, età, squadra, valore di mercato e storico carriera.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="op-label text-te8ff00">// DETTAGLI NON ANCORA SINCRONIZZATI</div>
                                    <p className="text-tc9ccd3 text-sm mt-2">
                                        Statistiche dettagliate, stile di gioco e foto Authentic verranno
                                        popolate non appena la fase Dettagli del sync arriva su questo
                                        giocatore.
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Stats — layout stile eFootball: 3 colonne (Attacco · Difesa · Forza),
                header in rosso/rosa con linea, numeri colorati a 3 fasce
                (verde ≥80 · giallo 70–79 · rosso ≤69). */}
            {hasDetails && Object.keys(stats).length > 0 && (
                <section className="mt-10" data-testid="player-stats">
                    <div className="op-label text-t2bb6ff mb-3">// STATISTICHE</div>
                    <div
                        className="op-card p-5 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8 max-w-5xl"
                        data-testid="stats-pesdb"
                    >
                        {STAT_COLUMNS_EF.map((col) => {
                            const rows = col.keys.filter((k) => stats[k] != null);
                            if (rows.length === 0) return null;
                            return (
                                <div key={col.title} data-testid={`stats-col-${col.title.toLowerCase()}`}>
                                    <div
                                        className="op-heading text-base uppercase tracking-wide pb-1.5 mb-2 border-b border-te0457b/40"
                                        style={{ color: "rgb(var(--tc-e0457b))" }}
                                    >
                                        {col.title}
                                    </div>
                                    {rows.map((k) => (
                                        <StatRow
                                            key={k}
                                            label={trStat(k)}
                                            value={stats[k]}
                                            highlight={REF_STATS.has(k) || REF_STATS_GK.has(k)}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    <div className="op-mono text-[11px] text-t8f95a3 mt-3 flex items-center gap-1.5" data-testid="stats-ref-legend">
                        <span style={{ color: "rgb(var(--tc-2bb6ff))" }}>◆</span> Valore authentic di riferimento
                    </div>
                </section>
            )}

            {/* Statistiche carriera (gol + migliore in campo) per stagione/squadra —
                CAMPIONATO + COPPE. Posizionata appena SOPRA lo storico carriera. */}
            {careerStats.length > 0 && (
                <section className="mt-10" data-testid="career-stats">
                    <div className="op-label text-te8ff00 mb-3">// STATISTICHE CARRIERA (OP)</div>
                    <div className="op-card overflow-x-auto">
                        <table className="w-full text-sm" data-testid="career-stats-table">
                            <thead>
                                <tr className="op-label text-[10px] text-t5c6370 border-b border-t22252a">
                                    <th className="text-left px-4 py-3">Stagione</th>
                                    <th className="text-left px-4 py-3">Squadra</th>
                                    <th className="text-center px-4 py-3">Gol</th>
                                    <th className="text-center px-4 py-3">Migliore in campo</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-t16181c">
                                {careerStats.map((s, i) => (
                                    <tr key={i} className="hover:bg-t16181c/40 transition-colors" data-testid={`career-stats-row-${i}`}>
                                        <td className="px-4 py-3">
                                            <span className="op-mono text-[11px] uppercase text-t5c6370">S{s.season}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {s.team_logo ? (
                                                    <img src={s.team_logo} alt="" className="w-6 h-6 object-contain shrink-0" />
                                                ) : (
                                                    <span className="w-6 h-6 shrink-0 rounded-full bg-t16181c border border-t22252a" />
                                                )}
                                                <span className="text-tc9ccd3 truncate">{s.team_name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center op-mono text-sm font-bold text-te8ff00">{s.goals}</td>
                                        <td className="px-4 py-3 text-center op-mono text-sm font-bold text-t2bb6ff">{s.mvp}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Storico carriera (OP) — in fondo, per ultimo. Unisce lo storico
                archiviato (permanenze passate / uscite verso Cina-Arabia / svincoli)
                con quello della permanenza attuale, così resta visibile anche per
                i giocatori che hanno lasciato il torneo. */}
            {(() => {
                const career = [...(p.archived_history || []), ...(p.op_team?.legacy_history || [])];
                if (career.length === 0) return null;
                return (
                <section className="mt-10" data-testid="legacy-history">
                    <div className="op-label text-t2bb6ff mb-3">// STORICO CARRIERA (OP)</div>
                    <div className="op-card p-4">
                        <div className="space-y-2">
                            {career.map((h, i) => {
                                const isExit = h.type === "Cessione estero" || h.type === "Svincolo";
                                return (
                                <div
                                    key={i}
                                    className={`flex items-center justify-between gap-3 text-sm border-l-2 pl-3 py-1 ${isExit ? "border-tff3b30/50" : "border-t2bb6ff/40"}`}
                                    data-testid={`legacy-history-row-${i}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="op-mono text-[11px] uppercase text-t5c6370 shrink-0">
                                            S{h.season}
                                        </span>
                                        <span className={`truncate ${isExit ? "text-tff8a80" : "text-tc9ccd3"}`}>{h.team || "—"}</span>
                                        {h.type && (
                                            <span className={`op-mono text-[10px] uppercase shrink-0 ${isExit ? "text-tff3b30" : "text-t8f95a3"}`}>
                                                {h.type}
                                            </span>
                                        )}
                                    </div>
                                    <div className="op-mono text-xs text-t2bb6ff font-bold whitespace-nowrap">
                                        {h.value != null ? formatMln(h.value) : "—"}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
                );
            })()}
        </div>
    );
}

function InfoCell({ label, value }) {
    return (
        <div className="p-4">
            <div className="op-label text-[10px]">{label}</div>
            <div className="text-tffffff font-semibold mt-1 text-sm">{value ?? "—"}</div>
        </div>
    );
}

function SkillsCard({ skills = [], aiStyles = [] }) {
    const cleanSkills = (Array.isArray(skills) ? skills : []).filter((s) => s && s !== "-");
    const cleanAi = (Array.isArray(aiStyles) ? aiStyles : []).filter((s) => s && s !== "-");
    const hasSkills = cleanSkills.length > 0;
    const hasAi = cleanAi.length > 0;
    return (
        <div className="op-card p-4" data-testid="player-skills-card">
            <div className="op-label text-te8ff00 mb-3">// ABILITÀ GIOCATORE</div>
            {!hasSkills && !hasAi ? (
                <div className="op-mono text-[11px] text-t8f95a3 italic" data-testid="player-skills-empty">
                    Nessuna abilità registrata.
                </div>
            ) : (
                <>
                    {hasSkills && (
                        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="player-skills-list">
                            {cleanSkills.map((s) => (
                                <span
                                    key={s}
                                    className="op-mono text-[11px] px-2 py-1 border border-te8ff00/30 text-te8ff00 bg-te8ff00/5"
                                    title={s}
                                >
                                    {trSkill(s)}
                                </span>
                            ))}
                        </div>
                    )}
                    {hasAi && (
                        <>
                            <div className="op-label text-[10px] text-t8f95a3 mb-1.5">Stili IA</div>
                            <div className="flex flex-wrap gap-1.5" data-testid="player-ai-styles-list">
                                {cleanAi.map((s) => (
                                    <span
                                        key={s}
                                        className="op-mono text-[11px] px-2 py-1 border border-t2bb6ff/30 text-t2bb6ff bg-t2bb6ff/5"
                                        title={s}
                                    >
                                        {trAiStyle(s)}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function SmallChip({ label, value, sub }) {
    return (
        <div className="op-card p-3">
            <div className="op-label text-[10px]">{label}</div>
            <div className="text-tffffff text-sm font-medium mt-1">{value}</div>
            {sub && <div className="op-mono text-[10px] text-tff7a1a mt-0.5">{sub}</div>}
        </div>
    );
}

function Highlight({ label, value, sub, accent }) {
    return (
        <div className="op-card p-4" style={{ borderLeft: `2px solid ${accent}` }}>
            <div className="op-label">{label}</div>
            <div className="text-tffffff font-bold mt-1" style={{ color: accent }}>{value}</div>
            {sub && <div className="op-mono text-xs text-t8f95a3 mt-0.5">{sub}</div>}
        </div>
    );
}

// Colori stile eFootball a 3 fasce:
//   ≥80 verde · 70–79 giallo · ≤69 rosso
function statColor(value) {
    if (value >= 80) return "rgb(var(--tc-5fd35f))";
    if (value >= 70) return "rgb(var(--tc-e8c34a))";
    return "rgb(var(--tc-e0533a))";
}

function StatRow({ label, value, highlight = false }) {
    const color = statColor(value);
    return (
        <div
            className="flex items-center justify-between py-[3px] border-b border-tffffff/[0.06] last:border-0"
            data-testid={`stat-row-${label}`}
        >
            <span className="text-[13px] text-tc9ccd3 flex items-center gap-1.5">
                {highlight && (
                    <span style={{ color: "rgb(var(--tc-2bb6ff))" }} className="text-[10px] leading-none" title="Valore authentic di riferimento" data-testid={`stat-ref-${label}`}>◆</span>
                )}
                {label}
            </span>
            <span className="op-mono font-bold text-[15px] tabular-nums" style={{ color }}>
                {value}
            </span>
        </div>
    );
}

function injuryColor(label) {
    if (!label) return "rgb(var(--tc-8f95a3))";
    const l = label.toLowerCase();
    if (l.includes("high") || l.includes("alta")) return "rgb(var(--tc-2bb6ff))";
    if (l.includes("medium") || l.includes("media")) return "rgb(var(--tc-e8ff00))";
    if (l.includes("low") || l.includes("bassa")) return "rgb(var(--tc-ff3b30))";
    return "rgb(var(--tc-8f95a3))";
}

function formatMln(v) {
    if (v == null) return "—";
    if (v === 0) return "0";
    const n = v / 1_000_000;
    if (n >= 100) return `${n.toFixed(0)}mln`;
    return `${n.toFixed(2)}mln`;
}

// ---- Confronto giocatore 1-vs-1 (sola lettura, riusa STAT_COLUMNS_EF/trStat) ----
function CmpRow({ label, a, b, numeric = true, fmt, diffFmt }) {
    const na = numeric && typeof a === "number" ? a : null;
    const nb = numeric && typeof b === "number" ? b : null;
    let aWin = false, bWin = false, diff = null;
    if (na != null && nb != null && na !== nb) {
        if (na > nb) { aWin = true; diff = na - nb; } else { bWin = true; diff = nb - na; }
    }
    const show = (v) => (v == null || v === "" ? "—" : (fmt ? fmt(v) : v));
    const diffStr = diff == null ? "" : (diffFmt ? diffFmt(diff) : `+${diff}`);
    return (
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-[3px] border-b border-tffffff/[0.06] last:border-0" data-testid={`cmp-row-${label}`}>
            <div className="text-right op-mono font-bold text-[14px] tabular-nums flex items-center justify-end gap-1.5" style={{ color: aWin ? "rgb(var(--tc-5fd35f))" : (bWin ? "rgb(var(--tc-6b7280))" : "rgb(var(--tc-c9ccd3))") }}>
                {aWin && <span className="text-[10px] text-t5fd35f">{diffStr}</span>}
                <span>{show(a)}</span>
            </div>
            <div className="text-center text-[11px] text-t8f95a3 px-1 min-w-[110px]">{label}</div>
            <div className="text-left op-mono font-bold text-[14px] tabular-nums flex items-center gap-1.5" style={{ color: bWin ? "rgb(var(--tc-5fd35f))" : (aWin ? "rgb(var(--tc-6b7280))" : "rgb(var(--tc-c9ccd3))") }}>
                <span>{show(b)}</span>
                {bWin && <span className="text-[10px] text-t5fd35f">{diffStr}</span>}
            </div>
        </div>
    );
}

function CmpPlayerHead({ pl, side }) {
    return (
        <div className={`min-w-0 ${side === "b" ? "text-left" : "text-right"}`}>
            <div className="op-heading text-lg text-tffffff truncate" data-testid={`compare-name-${side}`}>{pl.name}</div>
            <div className="op-mono text-[11px] text-t8f95a3">
                VG {pl.overall ?? "—"} · {trPositionShort(pl.position) || "—"}
                {pl.op_team?.name ? ` · ${pl.op_team.name}` : (pl.team_pes ? ` · ${pl.team_pes}` : "")}
            </div>
        </div>
    );
}

function ComparePanel({ playerA, onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [playerB, setPlayerB] = useState(null);
    const [loadingB, setLoadingB] = useState(false);

    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) { setResults([]); return; }
        setSearching(true);
        const t = setTimeout(() => {
            api.get("/search", { params: { q: term } })
                .then((r) => setResults(r.data?.players || []))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const pick = (pl) => {
        const id = pl.pes_id ?? pl.entry_id;
        if (id == null) return;
        setResults([]); setQ(""); setLoadingB(true);
        api.get(`/players/${id}`)
            .then((r) => setPlayerB(r.data))
            .catch(() => setPlayerB(null))
            .finally(() => setLoadingB(false));
    };

    const tmVal = (pl) => pl?.op_team?.market_value_current ?? pl?.op_team?.market_value ?? null;
    const statsA = playerA.stats || {};
    const statsB = playerB?.stats || {};
    const hasStats = playerB && (Object.keys(statsA).length > 0 || Object.keys(statsB).length > 0);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/75 overflow-y-auto" onClick={onClose} data-testid="compare-modal">
            <div className="op-card w-full max-w-3xl mt-8 mb-10 p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="op-label" style={{ color: "rgb(var(--tc-b98bff))" }}>// CONFRONTO GIOCATORI</div>
                    <button onClick={onClose} className="op-mono text-xs px-3 py-1.5 border border-t22252a text-t8f95a3 hover:text-tffffff hover:border-tffffff transition-colors" data-testid="compare-close">✕ Chiudi</button>
                </div>

                {/* Intestazione: A vs B */}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 pb-3 mb-3 border-b border-t22252a">
                    <CmpPlayerHead pl={playerA} side="a" />
                    <div className="op-mono text-[11px] text-tb98bff font-bold">VS</div>
                    {playerB ? (
                        <div className="flex items-center justify-start gap-2 min-w-0">
                            <CmpPlayerHead pl={playerB} side="b" />
                            <button onClick={() => setPlayerB(null)} className="op-mono text-[10px] text-t8f95a3 hover:text-tffffff underline shrink-0" data-testid="compare-change-b">cambia</button>
                        </div>
                    ) : (
                        <div className="op-mono text-[11px] text-t5c6370 text-left">Scegli un giocatore →</div>
                    )}
                </div>

                {/* Ricerca secondo giocatore */}
                {!playerB && (
                    <div data-testid="compare-search">
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca un giocatore (rosa, svincolati, PESDB)…"
                            className="op-input w-full"
                            data-testid="compare-search-input"
                        />
                        {searching && <div className="op-mono text-[11px] text-t8f95a3 mt-2">Ricerca…</div>}
                        <div className="mt-2 max-h-72 overflow-y-auto divide-y divide-t1a1d24" data-testid="compare-results">
                            {results.map((pl, i) => {
                                const id = pl.pes_id ?? pl.entry_id;
                                return (
                                    <button
                                        key={`${id}-${i}`}
                                        onClick={() => pick(pl)}
                                        className="w-full flex items-center justify-between gap-3 px-2 py-2 text-left hover:bg-t0f1115 transition-colors"
                                        data-testid={`compare-result-${id}`}
                                    >
                                        <span className="text-sm text-tffffff truncate">{pl.name}</span>
                                        <span className="op-mono text-[11px] text-t8f95a3 shrink-0">
                                            VG {pl.overall ?? "—"} · {trPositionShort(pl.position) || "—"}{pl.club ? ` · ${pl.club}` : ""}
                                        </span>
                                    </button>
                                );
                            })}
                            {!searching && q.trim().length >= 2 && results.length === 0 && (
                                <div className="op-mono text-[11px] text-t5c6370 py-3 text-center" data-testid="compare-noresults">Nessun giocatore trovato.</div>
                            )}
                        </div>
                    </div>
                )}

                {loadingB && <div className="op-mono text-xs text-t8f95a3 py-6 text-center">Caricamento scheda…</div>}

                {/* Confronto */}
                {playerB && !loadingB && (
                    <div data-testid="compare-content">
                        <div className="op-label text-t8f95a3 mb-1">// ANAGRAFICA</div>
                        <div className="op-card p-3 mb-5">
                            <CmpRow label="Overall (VG)" a={playerA.overall} b={playerB.overall} />
                            <CmpRow label="Età" a={playerA.age} b={playerB.age} diffFmt={(d) => `+${d}`} />
                            <CmpRow label="Altezza (cm)" a={playerA.height} b={playerB.height} />
                            <CmpRow label="Peso (kg)" a={playerA.weight} b={playerB.weight} />
                            <CmpRow label="Piede" a={trFoot(playerA.foot)} b={trFoot(playerB.foot)} numeric={false} />
                            <CmpRow label="Nazionalità" a={playerA.nationality} b={playerB.nationality} numeric={false} />
                            <CmpRow label="Ruolo" a={trPositionShort(playerA.position)} b={trPositionShort(playerB.position)} numeric={false} />
                            <CmpRow label="Stipendio" a={playerA.purchase_wage} b={playerB.purchase_wage} fmt={formatMln} diffFmt={formatMln} />
                            <CmpRow label="Valore TM" a={tmVal(playerA)} b={tmVal(playerB)} fmt={formatMln} diffFmt={formatMln} />
                        </div>

                        {hasStats ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6" data-testid="compare-stats">
                                {STAT_COLUMNS_EF.map((col) => {
                                    const keys = col.keys.filter((k) => statsA[k] != null || statsB[k] != null);
                                    if (keys.length === 0) return null;
                                    return (
                                        <div key={col.title} data-testid={`compare-stats-col-${col.title.toLowerCase()}`}>
                                            <div className="op-heading text-sm uppercase tracking-wide pb-1.5 mb-1.5 border-b border-te0457b/40" style={{ color: "rgb(var(--tc-e0457b))" }}>{col.title}</div>
                                            {keys.map((k) => (
                                                <CmpRow key={k} label={trStat(k)} a={statsA[k] ?? null} b={statsB[k] ?? null} />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="op-mono text-[11px] text-t8f95a3 py-3 text-center" data-testid="compare-nostats">
                                Statistiche PESDB non disponibili per uno dei due giocatori.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function OpTeamBanner({ op }) {
    if (!op) {
        return (
            <div
                className="mb-6 inline-flex items-center gap-3 px-4 py-2 border border-t8f95a3/40 bg-t1a1d24"
                data-testid="player-op-team-banner-free"
            >
                <span className="op-mono text-[10px] uppercase text-t8f95a3">Status Torneo</span>
                <span className="op-heading text-base text-tffffff">FREE TRANSFER</span>
            </div>
        );
    }
    const accent = op.is_duplicate
        ? "rgb(var(--tc-e8ff00))"
        : op.is_loaned_out
            ? "rgb(var(--tc-8f95a3))"
            : op.is_ugly
                ? "rgb(var(--tc-ff6b00))"
                : "rgb(var(--tc-2bb6ff))";
    let subtitle = "Original PES";
    if (op.is_duplicate) subtitle = "Carta doppione · giocatore in rosa con altra carta";
    else if (op.is_loaned_out) subtitle = "In prestito · proprietà di";
    else if (op.is_ugly) subtitle = "Settore Ugly";
    return (
        <Link
            to={`/squadre/${op.id}`}
            className="mb-6 inline-flex items-center gap-3 px-4 py-2 border bg-t1a1d24 hover:bg-t22252a transition-colors group"
            style={{ borderColor: withAlpha(accent, "55") }}
            data-testid="player-op-team-banner"
        >
            {op.logo_url && (
                <img
                    src={op.logo_url}
                    alt={op.name}
                    className="w-8 h-8 object-contain bg-tffffff rounded-sm p-0.5"
                    loading="lazy"
                />
            )}
            <div>
                <div className="op-mono text-[10px] uppercase text-t8f95a3">
                    {subtitle}
                </div>
                <div className="op-heading text-base text-tffffff group-hover:text-t2bb6ff transition-colors">
                    {op.name}
                </div>
            </div>
        </Link>
    );
}

// Pannello admin sulla scheda: stato PESDB + aggancia/ri-aggancia per ID + scollega + cerca per nome.
function AdminPesdbPanel({ teamId, entryId, currentPesId, playerName, currentTmLink, currentStatuses = [], currentOnLoan = false, currentLoanedOut = false, currentLoanKind = "", currentStyleAtt = "", currentStyleDef = "", deadPesdb, noPesdb, onChanged }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [pesId, setPesId] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState("");
    const [q, setQ] = useState(playerName || "");
    const [searching, setSearching] = useState(false);
    const [cands, setCands] = useState(null);
    // Sezione TM / nome
    const [nameVal, setNameVal] = useState(playerName || "");
    const [tmLink, setTmLink] = useState(currentTmLink || "");
    const [tmAge, setTmAge] = useState("");
    const [tmRefetch, setTmRefetch] = useState(false);
    const [tmValueMln, setTmValueMln] = useState("");
    // Sezione stile di gioco (doppio stile Att/Dif)
    const [styleAtt, setStyleAtt] = useState(currentStyleAtt || "");
    const [styleDef, setStyleDef] = useState(currentStyleDef || "");
    // Sezione Tag/Stati (Primavera, Ugly, NFC, SVI)
    const [stat, setStat] = useState(() => new Set(currentStatuses || []));

    const linked = currentPesId != null && !deadPesdb && !noPesdb;
    const statusLabel = deadPesdb
        ? `⚠️ ID PESDB morto (${currentPesId}) — da ri-agganciare`
        : noPesdb
            ? "Senza scheda PESDB"
            : `Agganciato a PESDB · ID ${currentPesId}`;
    const statusColor = deadPesdb ? "rgb(var(--tc-ff9d2b))" : noPesdb ? "rgb(var(--tc-8f95a3))" : "rgb(var(--tc-7ce38b))";

    const attach = async (id) => {
        const val = parseInt(id ?? pesId, 10);
        if (!val || val <= 0) { setErr("Inserisci un ID PESDB valido"); return; }
        setBusy(true); setErr(""); setOk("");
        try {
            const { data } = await api.put(`/teams/${teamId}/roster/${entryId}/admin-pesid`, { pes_id: val });
            setOk(`Agganciato: ${data.name || ""} (VG ${data.overall ?? "—"} · ${data.position || "—"}) — apro la scheda completa…`);
            setPesId(""); setCands(null);
            onChanged?.();
            // Porta subito alla scheda del NUOVO id, dove ci sono tutti i dati (abilità,
            // statistiche, stili). Restando sul vecchio url si vedrebbe solo il ripiego (VG+foto).
            if (data.pes_id) {
                setTimeout(() => navigate(`/giocatori/${data.pes_id}`), 600);
            }
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante l'aggancio (in produzione lo scraping è bloccato: fallo in preview).");
        } finally { setBusy(false); }
    };

    const unlink = async () => {
        if (!window.confirm(`Scollegare ${playerName} dalla scheda PESDB?\n\nResterà in rosa col proprio nome/età ma senza scheda PESDB.`)) return;
        setBusy(true); setErr(""); setOk("");
        try {
            await api.post(`/teams/${teamId}/roster/${entryId}/admin-unlink-pesdb`);
            setOk("Scollegato da PESDB — apro la scheda senza PESDB…");
            onChanged?.();
            // La scheda aperta per pes_id continuerebbe a mostrare la carta PESDB (il
            // documento players esiste ancora ed è agganciato per nome). Porto subito
            // alla scheda della VOCE DI ROSA (entry_id) = stato "senza PESDB", inverso
            // dell'aggancio (che naviga al nuovo pes_id).
            setTimeout(() => navigate(`/giocatori/${entryId}`), 600);
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante lo scollegamento");
        } finally { setBusy(false); }
    };

    const search = async () => {
        const name = (q || "").trim();
        if (name.length < 2) { setErr("Nome troppo corto"); return; }
        setSearching(true); setErr(""); setCands(null);
        try {
            const { data } = await api.get(`/admin/pesdb-relink/search`, { params: { name } });
            setCands(data.candidates || []);
        } catch (e) {
            setErr(e.response?.data?.detail || "Ricerca non disponibile (scraping bloccato in produzione).");
        } finally { setSearching(false); }
    };

    const saveName = async () => {
        const v = (nameVal || "").trim();
        if (v.length < 1) { setErr("Nome non valido"); return; }
        setBusy(true); setErr(""); setOk("");
        try {
            await api.put(`/teams/${teamId}/roster/${entryId}/admin-name`, { name: v });
            setOk("Nome aggiornato.");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante il salvataggio del nome");
        } finally { setBusy(false); }
    };

    const saveTm = async () => {
        setBusy(true); setErr(""); setOk("");
        try {
            const body = {
                tm_link: (tmLink || "").trim() || null,
                tm_age: tmAge !== "" ? parseInt(tmAge, 10) : null,
                refetch: !!tmRefetch,
            };
            await api.put(`/teams/${teamId}/roster/${entryId}/admin-tm`, body);
            setOk("Transfermarkt aggiornato.");
            setTmAge("");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante il salvataggio TM");
        } finally { setBusy(false); }
    };

    const saveTmValueManual = async () => {
        const v = parseFloat(String(tmValueMln).replace(",", "."));
        if (!(v >= 0)) { setErr("Inserisci un valore TM valido in milioni (es. 45)"); return; }
        setBusy(true); setErr(""); setOk("");
        try {
            await api.put(`/teams/${teamId}/roster/${entryId}/admin-tm`, { market_value_mln: v });
            setOk(`Valore TM impostato a mano: ${v} mln (agg. a oggi).`);
            setTmValueMln("");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante il salvataggio del valore TM");
        } finally { setBusy(false); }
    };

    const clearLoan = async () => {
        if (!window.confirm(`Rimuovere lo stato di PRESTITO da ${playerName}?\n\nResterà in questa squadra come giocatore DEFINITIVO (sparisce il badge "in prestito").`)) return;
        setBusy(true); setErr(""); setOk("");
        try {
            const { data } = await api.post(`/teams/${teamId}/roster/${entryId}/admin-clear-loan`);
            setOk(data.message || "Stato di prestito rimosso.");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante la rimozione dello stato di prestito");
        } finally { setBusy(false); }
    };

    const saveStyles = async () => {
        setBusy(true); setErr(""); setOk("");
        try {
            await api.put(`/teams/${teamId}/roster/${entryId}/admin-styles`, {
                playing_style_att: styleAtt || "",
                playing_style_def: styleDef || "",
            });
            setOk("Stile di gioco aggiornato.");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante il salvataggio dello stile");
        } finally { setBusy(false); }
    };

    const refetchStyles = async () => {
        setBusy(true); setErr(""); setOk("");
        try {
            const { data } = await api.post(`/teams/${teamId}/roster/${entryId}/admin-refetch-styles`);
            setStyleAtt(data.playing_style_att || "");
            setStyleDef(data.playing_style_def || "");
            setOk("Stile riletto da PESDB.");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Rilettura non disponibile (in produzione lo scraping è bloccato).");
        } finally { setBusy(false); }
    };

    const refetchTmSingle = async (field) => {
        const okMap = {
            name: "Nome aggiornato da Transfermarkt.",
            age: "Età aggiornata da Transfermarkt.",
            value: "Prezzo TM aggiornato da Transfermarkt.",
        };
        setBusy(true); setErr(""); setOk("");
        try {
            await api.post(`/teams/${teamId}/roster/${entryId}/admin-refetch-tm/${field}`);
            setOk(okMap[field] || "Aggiornato da TM.");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Aggiornamento TM non riuscito (TM sta bloccando l'IP?).");
        } finally { setBusy(false); }
    };

    const refetchSingle = async (what) => {
        const map = {
            stats: { url: "admin-refetch-stats", ok: "Statistiche aggiornate da PESDB." },
            skills: { url: "admin-refetch-skills", ok: "Abilità giocatore aggiornate da PESDB." },
            positions: { url: "admin-refetch-position-ratings", ok: "Mappa ruoli aggiornata da PESDB." },
            aistyles: { url: "admin-refetch-ai-styles", ok: "Stili di gioco AI aggiornati da PESDB." },
        }[what];
        setBusy(true); setErr(""); setOk("");
        try {
            await api.post(`/teams/${teamId}/roster/${entryId}/${map.url}`);
            setOk(map.ok);
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Aggiornamento non riuscito.");
        } finally { setBusy(false); }
    };

    const toggleStat = (code) => {
        setStat((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    };

    const saveStatuses = async () => {
        setBusy(true); setErr(""); setOk("");
        try {
            await api.put(`/teams/${teamId}/roster/${entryId}/admin-statuses`, { statuses: Array.from(stat) });
            setOk("Tag aggiornati.");
            onChanged?.();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore durante il salvataggio dei tag");
        } finally { setBusy(false); }
    };

    if (!open) {
        return (
            <button
                onClick={() => setOpen(true)}
                className="mb-6 ml-3 inline-flex items-center gap-2 px-4 py-2 border border-t9d7cff/50 bg-t9d7cff/10 hover:bg-t9d7cff/20 transition-colors align-top"
                data-testid="admin-pesdb-panel-open"
            >
                <span className="op-mono text-[10px] uppercase text-t8f95a3">Admin</span>
                <span className="op-heading text-sm text-t9d7cff">🔗 GESTISCI PESDB</span>
            </button>
        );
    }

    return (
        <div className="mb-6 ml-3 inline-block w-full max-w-md p-4 border border-t9d7cff/40 bg-t0e1014 align-top" data-testid="admin-pesdb-panel">
            <div className="flex items-center justify-between mb-3">
                <span className="op-label text-t9d7cff">// PESDB (ADMIN)</span>
                <button onClick={() => setOpen(false)} className="op-mono text-[10px] text-t8f95a3 hover:text-tffffff" data-testid="admin-pesdb-close">✕ chiudi</button>
            </div>
            <div className="op-mono text-[11px] mb-3" style={{ color: statusColor }} data-testid="admin-pesdb-status">{statusLabel}</div>

            <div className="flex items-center gap-2 mb-2">
                <input
                    type="number" min="1"
                    value={pesId}
                    onChange={(e) => setPesId(e.target.value)}
                    placeholder="ID PESDB"
                    className="flex-1 bg-t0a0b0d border border-t22252a focus:border-t9d7cff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                    data-testid="admin-pesdb-id-input"
                />
                <button
                    onClick={() => attach()}
                    disabled={busy}
                    className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t7ce38b/50 text-t7ce38b hover:bg-t7ce38b/10 transition-colors disabled:opacity-40"
                    data-testid="admin-pesdb-attach"
                >
                    {busy ? "..." : (linked ? "Ri-aggancia" : "Aggancia")}
                </button>
            </div>

            <div className="flex items-center gap-2 mb-3">
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Cerca per nome su PESDB"
                    className="flex-1 bg-t0a0b0d border border-t22252a focus:border-t9d7cff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                    data-testid="admin-pesdb-search-input"
                />
                <button
                    onClick={search}
                    disabled={searching}
                    className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t9d7cff/50 text-t9d7cff hover:bg-t9d7cff/10 transition-colors disabled:opacity-40"
                    data-testid="admin-pesdb-search-btn"
                >
                    {searching ? "..." : "Cerca"}
                </button>
            </div>

            {cands != null && (
                <div className="mb-3 max-h-52 overflow-y-auto border border-t22252a" data-testid="admin-pesdb-candidates">
                    {cands.length === 0 ? (
                        <div className="op-mono text-[11px] text-t8f95a3 p-2">Nessun candidato trovato.</div>
                    ) : cands.map((c) => (
                        <button
                            key={c.pes_id}
                            onClick={() => attach(c.pes_id)}
                            disabled={busy}
                            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 border-b border-t1a1d24 hover:bg-t9d7cff/10 transition-colors text-left disabled:opacity-40"
                            data-testid={`admin-pesdb-cand-${c.pes_id}`}
                        >
                            <span className="op-mono text-[11px] text-tffffff truncate">{c.name}</span>
                            <span className="op-mono text-[10px] text-t8f95a3 shrink-0">
                                {c.position || "—"} · VG {c.overall ?? "—"} · {c.age ?? "—"}a · #{c.pes_id}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {linked && (
                <button
                    onClick={unlink}
                    disabled={busy}
                    className="op-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border border-tff9d2b/50 text-tff9d2b hover:bg-tff9d2b/10 transition-colors disabled:opacity-40"
                    data-testid="admin-pesdb-unlink"
                >
                    🔗 Scollega da PESDB
                </button>
            )}

            {/* Nome giocatore */}
            <div className="border-t border-t22252a mt-3 pt-3">
                <div className="op-label text-[10px] mb-2">Nome giocatore</div>
                <div className="flex items-center gap-2">
                    <input
                        type="text" maxLength={80}
                        value={nameVal}
                        onChange={(e) => setNameVal(e.target.value)}
                        className="flex-1 bg-t0a0b0d border border-t22252a focus:border-t2bb6ff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                        data-testid="admin-name-input"
                    />
                    <button
                        onClick={saveName}
                        disabled={busy}
                        className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t2bb6ff/50 text-t2bb6ff hover:bg-t2bb6ff/10 transition-colors disabled:opacity-40"
                        data-testid="admin-name-save"
                    >
                        Salva
                    </button>
                </div>
            </div>

            {/* Transfermarkt · link & età */}
            <div className="border-t border-t22252a mt-3 pt-3">
                <div className="op-label text-[10px] mb-2">Transfermarkt · link & età</div>
                <input
                    type="text"
                    value={tmLink}
                    onChange={(e) => setTmLink(e.target.value)}
                    placeholder="URL profilo Transfermarkt (vuoto = rimuovi)"
                    className="w-full bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono mb-2"
                    data-testid="admin-tm-link-input"
                />
                <div className="flex items-center gap-2 mb-2">
                    <input
                        type="number" min="14" max="60"
                        value={tmAge}
                        onChange={(e) => setTmAge(e.target.value)}
                        placeholder="Età manuale (opz.)"
                        className="w-40 bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                        data-testid="admin-tm-age-input"
                    />
                    <label className="flex items-center gap-1.5 op-mono text-[10px] text-t8f95a3 cursor-pointer select-none">
                        <input type="checkbox" checked={tmRefetch} onChange={(e) => setTmRefetch(e.target.checked)} data-testid="admin-tm-refetch" />
                        Rileggi da TM (preview)
                    </label>
                </div>
                <button
                    onClick={saveTm}
                    disabled={busy}
                    className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t00bfff/50 text-t00bfff hover:bg-t00bfff/10 transition-colors disabled:opacity-40"
                    data-testid="admin-tm-save"
                >
                    Salva TM
                </button>
                {/* Rilettura SINGOLA da Transfermarkt: nome / età / prezzo (una richiesta ciascuno) */}
                <div className="mt-3">
                    <div className="op-mono text-[9px] text-t5c6370 mb-2 leading-relaxed">
                        Rilegge da TM SOLO il dato scelto (una richiesta per volta). Funziona ovunque TM
                        risponda; in produzione potrebbe bloccare l&apos;IP.
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => refetchTmSingle("name")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t00bfff/50 text-t00bfff hover:bg-t00bfff/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-tm-name"
                        >
                            ↻ Solo nome (TM)
                        </button>
                        <button
                            onClick={() => refetchTmSingle("age")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t00bfff/50 text-t00bfff hover:bg-t00bfff/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-tm-age"
                        >
                            ↻ Solo età (TM)
                        </button>
                        <button
                            onClick={() => refetchTmSingle("value")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t00bfff/50 text-t00bfff hover:bg-t00bfff/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-tm-value"
                        >
                            ↻ Solo prezzo TM
                        </button>
                    </div>
                </div>
                {/* Valore TM MANUALE (in milioni) — funziona anche in produzione (nessuno scraping) */}
                <div className="mt-3 border-t border-t22252a pt-3">
                    <div className="op-label text-[10px] mb-1">Valore TM manuale (milioni)</div>
                    <div className="op-mono text-[9px] text-t5c6370 mb-2 leading-relaxed">
                        Scrivi il valore preso da Transfermarkt in milioni (es. 45 = 45.000.000 €).
                        Funziona anche in produzione, senza passare dal preview. La data viene messa a oggi.
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number" min="0" step="0.1"
                            value={tmValueMln}
                            onChange={(e) => setTmValueMln(e.target.value)}
                            placeholder="es. 45"
                            className="w-40 bg-t0a0b0d border border-t22252a focus:border-t00bfff outline-none text-tffffff text-sm px-3 py-2 op-mono"
                            data-testid="admin-tm-value-input"
                        />
                        <span className="op-mono text-[11px] text-t8f95a3">mln €</span>
                        <button
                            onClick={saveTmValueManual}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t00bfff/50 text-t00bfff hover:bg-t00bfff/10 transition-colors disabled:opacity-40"
                            data-testid="admin-tm-value-save"
                        >
                            Salva valore
                        </button>
                    </div>
                </div>
            </div>

            {/* Stato prestito — rimozione manuale del badge "in prestito" (ripara voci incoerenti) */}
            {(currentOnLoan || currentLoanedOut) && (
                <div className="border-t border-t22252a mt-3 pt-3" data-testid="admin-loan-section">
                    <div className="op-label text-[10px] mb-2">Stato prestito</div>
                    <div className="op-mono text-[10px] text-tff9d2b mb-2">
                        {currentOnLoan ? "🔁 Risulta preso IN PRESTITO" : "🔁 Risulta DATO in prestito"}
                        {currentLoanKind ? ` · ${currentLoanKind}` : ""}
                    </div>
                    <button
                        onClick={clearLoan}
                        disabled={busy}
                        className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-tff9d2b/50 text-tff9d2b hover:bg-tff9d2b/10 transition-colors disabled:opacity-40"
                        data-testid="admin-clear-loan"
                    >
                        Rimuovi stato prestito
                    </button>
                    <div className="op-mono text-[9px] text-t5c6370 mt-2 leading-relaxed">
                        Toglie il badge "in prestito": il giocatore resta in questa squadra come DEFINITIVO.
                    </div>
                </div>
            )}

            {/* Stile di gioco (doppio stile Att/Dif) — funziona anche in produzione (manuale) */}
            <div className="border-t border-t22252a mt-3 pt-3" data-testid="admin-styles-section">
                <div className="op-label text-[10px] mb-2">Stile di gioco (Attacco / Difesa)</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                        <div className="op-mono text-[9px] text-t8f95a3 mb-1 uppercase">Attacco</div>
                        <select
                            value={styleAtt}
                            onChange={(e) => setStyleAtt(e.target.value)}
                            className="w-full bg-t0a0b0d border border-t22252a focus:border-te8ff00 outline-none text-tffffff text-sm px-2 py-2 op-mono"
                            data-testid="admin-style-att-select"
                        >
                            <option value="">— (nessuno)</option>
                            <option value="Basic">Basic</option>
                            {STYLE_OPTIONS.map((s) => (
                                <option key={`att-${s.value}`} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <div className="op-mono text-[9px] text-t8f95a3 mb-1 uppercase">Difesa</div>
                        <select
                            value={styleDef}
                            onChange={(e) => setStyleDef(e.target.value)}
                            className="w-full bg-t0a0b0d border border-t22252a focus:border-te8ff00 outline-none text-tffffff text-sm px-2 py-2 op-mono"
                            data-testid="admin-style-def-select"
                        >
                            <option value="">— (nessuno)</option>
                            <option value="Basic">Basic</option>
                            {STYLE_OPTIONS.map((s) => (
                                <option key={`def-${s.value}`} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={saveStyles}
                        disabled={busy}
                        className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-te8ff00/50 text-te8ff00 hover:bg-te8ff00/10 transition-colors disabled:opacity-40"
                        data-testid="admin-styles-save"
                    >
                        Salva stile
                    </button>
                    <button
                        onClick={refetchStyles}
                        disabled={busy}
                        className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t9d7cff/50 text-t9d7cff hover:bg-t9d7cff/10 transition-colors disabled:opacity-40"
                        data-testid="admin-styles-refetch"
                        title="Funziona solo in preview (in produzione lo scraping è bloccato)"
                    >
                        ↻ Rileggi da PESDB
                    </button>
                </div>
                <div className="op-mono text-[9px] text-t5c6370 mt-2 leading-relaxed">
                    La rosa mostra lo stile NON-&quot;Basic&quot; (priorità attacco). Il salvataggio manuale funziona anche in PRODUZIONE.
                    &quot;Rileggi da PESDB&quot; solo in preview.
                </div>
            </div>

            {/* Aggiornamento SINGOLO da PESDB: stats / abilità / mappa ruoli (non tocca il VG) */}
            {linked && (
                <div className="border-t border-t22252a mt-3 pt-3" data-testid="admin-refetch-single-section">
                    <div className="op-label text-t2bb6ff mb-1">// AGGIORNA SINGOLO DA PESDB</div>
                    <div className="op-mono text-[9px] text-t5c6370 mb-2 leading-relaxed">
                        Rilegge da PESDB solo il dato scelto, senza toccare il VG né il resto.
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => refetchSingle("stats")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t2bb6ff/50 text-t2bb6ff hover:bg-t2bb6ff/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-stats"
                        >
                            ↻ Solo statistiche
                        </button>
                        <button
                            onClick={() => refetchSingle("skills")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t7ce38b/50 text-t7ce38b hover:bg-t7ce38b/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-skills"
                        >
                            ↻ Solo abilità giocatore
                        </button>
                        <button
                            onClick={() => refetchSingle("positions")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-tff9d2b/50 text-tff9d2b hover:bg-tff9d2b/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-positions"
                        >
                            ↻ Solo mappa ruoli
                        </button>
                        <button
                            onClick={() => refetchSingle("aistyles")}
                            disabled={busy}
                            className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-t9d7cff/50 text-t9d7cff hover:bg-t9d7cff/10 transition-colors disabled:opacity-40"
                            data-testid="admin-refetch-ai-styles"
                        >
                            ↻ Solo stili di gioco AI
                        </button>
                    </div>
                </div>
            )}

            {/* Tag / Stati (Primavera, Ugly, NFC, SVI) */}
            <div className="border-t border-t22252a mt-3 pt-3" data-testid="admin-statuses-section">
                <div className="op-label text-[10px] mb-2">Tag giocatore</div>
                <div className="flex flex-wrap gap-2 mb-2">
                    {STATUS_OPTIONS.map((s) => {
                        const active = stat.has(s.code);
                        return (
                            <button
                                key={s.code}
                                type="button"
                                onClick={() => toggleStat(s.code)}
                                className={`op-mono text-[11px] px-3 py-1.5 border transition-colors ${active ? "border-te8ff00 text-te8ff00 bg-te8ff00/10" : "border-t22252a text-t8f95a3 hover:border-t8f95a3"}`}
                                data-testid={`admin-status-toggle-${s.code}`}
                            >
                                {active ? "✓ " : ""}{s.label}
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={saveStatuses}
                    disabled={busy}
                    className="op-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-te8ff00/50 text-te8ff00 hover:bg-te8ff00/10 transition-colors disabled:opacity-40"
                    data-testid="admin-statuses-save"
                >
                    Salva tag
                </button>
                <div className="op-mono text-[9px] text-t5c6370 mt-2 leading-relaxed">
                    &quot;Ugly&quot; sposta il giocatore nel settore giovanile. &quot;Primavera (PRIM)&quot; lo rende eleggibile alla competizione Primavera.
                </div>
            </div>

            {err && <div className="op-mono text-[10px] text-tff3b30 mt-2" data-testid="admin-pesdb-error">{err}</div>}
            {ok && <div className="op-mono text-[10px] text-t7ce38b mt-2" data-testid="admin-pesdb-ok">{ok}</div>}
            <div className="op-mono text-[9px] text-t5c6370 mt-2 leading-relaxed">
                In produzione lo scraping è bloccato: aggancia in preview, poi porta tutto con Esporta/Importa ri-aganci.
            </div>
        </div>
    );
}

// Pulsanti "Richiedi agg. TM/PESDB" sulla scheda (presidente della squadra o admin).
function RequestUpdateOnCard({ teamId, entryId, pesId, tmLink, name }) {
    const [pendingKinds, setPendingKinds] = useState([]);

    const reload = () => {
        api.get(`/teams/${teamId}/roster/update-requests`)
            .then((r) => {
                const ks = (r.data?.pending || [])
                    .filter((d) => d.entry_id === entryId)
                    .map((d) => d.kind);
                setPendingKinds(ks);
            })
            .catch(() => {});
    };
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamId, entryId]);

    const shaped = {
        entry_id: entryId,
        pes_id: pesId,
        has_pes_id: pesId != null,
        tm_link: tmLink,
        name,
    };

    return (
        <div className="mb-6 ml-3 inline-block align-top p-3 border border-t9d7cff/30 bg-t0e1014" data-testid="card-request-update">
            <div className="op-mono text-[9px] uppercase text-t8f95a3 mb-2">Richiedi aggiornamento dati</div>
            <RequestUpdateButtons p={shaped} teamId={teamId} pendingKinds={pendingKinds} onRequested={reload} />
        </div>
    );
}


